import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faCircleCheck,
  faCircleXmark,
  faUserMinus,
  faLock,
  faLockOpen,
  faArrowRightArrowLeft,
  faStar,
  faBell,
  faCheckDouble,
} from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { useNotificationStore } from '../../stores/notificationStore';
import apiService from '../../services/apiService';
import type { InAppNotification } from '../../stores/notificationStore';

function getNotificationIcon(type: string) {
  switch (type) {
    case 'join_request': return { icon: faUserPlus, color: 'text-blue-400', bg: 'bg-blue-400/10' };
    case 'member_accepted': return { icon: faCircleCheck, color: 'text-[#13EC5B]', bg: 'bg-[#13EC5B]/10' };
    case 'member_rejected': return { icon: faCircleXmark, color: 'text-red-400', bg: 'bg-red-400/10' };
    case 'member_removed': return { icon: faUserMinus, color: 'text-red-400', bg: 'bg-red-400/10' };
    case 'member_joined': return { icon: faUserPlus, color: 'text-[#13EC5B]', bg: 'bg-[#13EC5B]/10' };
    case 'contest_locked': return { icon: faLock, color: 'text-amber-400', bg: 'bg-amber-400/10' };
    case 'contest_unlocked': return { icon: faLockOpen, color: 'text-[#13EC5B]', bg: 'bg-[#13EC5B]/10' };
    case 'transfer_completed': return { icon: faArrowRightArrowLeft, color: 'text-blue-400', bg: 'bg-blue-400/10' };
    case 'points_updated': return { icon: faStar, color: 'text-amber-400', bg: 'bg-amber-400/10' };
    default: return { icon: faBell, color: 'text-gray-400', bg: 'bg-white/5' };
  }
}

function NotificationItem({ notification, onClose }: { notification: InAppNotification; onClose: () => void }) {
  const navigate = useNavigate();
  const { markAsRead } = useNotificationStore();
  const { icon, color, bg } = getNotificationIcon(notification.type);

  const handleClick = async () => {
    if (!notification.read) {
      markAsRead(notification.id);
      apiService.markNotificationAsRead(notification.id).catch(() => {});
    }
    if (!notification.contestId) return;
    onClose();

    // Navigate to the right page based on notification type
    const cid = notification.contestId;
    switch (notification.type) {
      case 'join_request':
        // Owner sees join request → go to members tab
        navigate(`/contest/${cid}?tab=members`);
        break;
      case 'member_accepted':
      case 'member_joined':
        // User got accepted → go to contest detail
        navigate(`/contest/${cid}`);
        break;
      case 'member_rejected':
      case 'member_removed':
        // User got rejected/removed → go to my contests
        navigate('/my-contests');
        break;
      case 'contest_locked':
      case 'contest_unlocked':
        // Lock status changed → go to contest teams tab
        navigate(`/contest/${cid}?tab=teams`);
        break;
      case 'transfer_completed':
        // Transfer done → go to contest teams tab
        navigate(`/contest/${cid}?tab=teams`);
        break;
      case 'points_updated':
        // Points updated → go to contest points tab
        navigate(`/contest/${cid}?tab=points`);
        break;
      default:
        navigate(`/contest/${cid}`);
    }
  };

  const timeAgo = (() => {
    try {
      return formatDistanceToNowStrict(parseISO(notification.createdAt), { addSuffix: true });
    } catch {
      return '';
    }
  })();

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.03] ${
        !notification.read ? 'bg-[#13EC5B]/[0.02]' : ''
      }`}
    >
      <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <FontAwesomeIcon icon={icon} className={`${color} text-xs`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-[13px] font-medium truncate ${!notification.read ? 'text-white' : 'text-gray-400'}`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="w-1.5 h-1.5 rounded-full bg-[#13EC5B] flex-shrink-0" />
          )}
        </div>
        <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">{notification.message}</p>
        <p className="text-[10px] text-gray-600 mt-1">{timeAgo}</p>
      </div>
    </button>
  );
}

export function NotificationPanel({ onClose }: { onClose: () => void }) {
  const { notifications, unreadCount, markAllAsRead } = useNotificationStore();

  const handleMarkAllRead = async () => {
    markAllAsRead();
    apiService.markAllNotificationsAsRead().catch(() => {});
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[#1A1D23] border border-[#2E3340] rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3340]">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {unreadCount > 0 && (
              <span className="text-[10px] font-bold text-[#13EC5B] bg-[#13EC5B]/10 px-1.5 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-[11px] text-gray-500 hover:text-[#13EC5B] transition-colors flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faCheckDouble} className="text-[9px]" />
              Mark all read
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[400px] overflow-y-auto divide-y divide-[#2E3340]/50">
          {notifications.length === 0 ? (
            <div className="py-12 text-center">
              <FontAwesomeIcon icon={faBell} className="text-2xl text-gray-600 mb-2" />
              <p className="text-sm text-gray-500">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onClose={onClose} />
            ))
          )}
        </div>
      </div>
    </>
  );
}
